import { ComponentType, ReactElement, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import Highlighter from 'react-highlight-words';
import ReactSelect, {
  ActionMeta,
  ControlProps,
  FormatOptionLabelMeta,
  IndicatorProps,
  MultiValueProps,
  OptionProps,
  SingleValueProps,
  ValueContainerProps,
  ValueType,
  components,
} from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { GroupHeadingProps } from 'react-select/src/components/Group';
import { MenuListComponentProps } from 'react-select/src/components/Menu';
import { CellMeasurer, CellMeasurerCache } from 'react-virtualized/dist/es/CellMeasurer';
import { List, ListRowRenderer } from 'react-virtualized/dist/es/List';

import IconButton from '@material-ui/core/IconButton';
import Typography from '@material-ui/core/Typography';
import ArrowDropDown from '@material-ui/icons/ArrowDropDown';
import clsx from 'clsx';

import { CloseIcon, colors } from '../../assets';
import { genericMemo } from '../../utils';
import { UserIdentifier } from '../userIdentifier/UserIdentifier';
import { selectStyles, useStyles } from './NewSelect.styles';
import { GroupOptionType, NewSelectProps, SelectOptionType } from './NewSelect.types';

const OPTION_LINE_HEIGHT = 20; // px per line
const OPTION_VERTICAL_PADDING = 20; // top + bottom padding total
const AVG_CHAR_WIDTH = 7.5; // approximate px per character at ~14px font size
const OPTION_HORIZONTAL_PADDING = 24; // left + right padding inside option

const estimateOptionHeight = (label: string, containerWidth: number): number => {
  if (!containerWidth || !label) return 40;
  const usableWidth = Math.max(1, containerWidth - OPTION_HORIZONTAL_PADDING);
  const charsPerLine = Math.max(1, Math.floor(usableWidth / AVG_CHAR_WIDTH));
  const lines = Math.ceil(label.length / charsPerLine);
  return Math.max(40, lines * OPTION_LINE_HEIGHT + OPTION_VERTICAL_PADDING);
};

const FormatOptionLabel = genericMemo(
  <T, S extends boolean = false>(
    { label }: SelectOptionType<unknown>,
    { inputValue }: FormatOptionLabelMeta<SelectOptionType<T>, S>,
  ) => {
    if (!inputValue) {
      return label;
    }

    return (
      <Highlighter
        autoEscape // it turns off treating 'searchWords' as regex (which can results in library errors and app crash)
        highlightStyle={{ color: colors.secondary.main }}
        searchWords={[inputValue]}
        textToHighlight={label}
        highlightTag="span"
      />
    );
  },
);

const Control = genericMemo(
  <T, S extends boolean = false>({
    children,
    classes: selectClasses,
    dataTestId,
    ...props
  }: ControlProps<SelectOptionType<T>, S> & {
    classes: NewSelectProps<T, S>['classes'];
    forFilter?: boolean;
    dataTestId?: string;
  }) => {
    const classes = useStyles({
      isMulti: props.isMulti,
      hasLabel: props.selectProps.label,
      startAdornmentWidth: props.selectProps.startAdornmentWidth,
    });

    const shouldFloatLabel = useMemo(() => {
      return (
        props.selectProps.label &&
        (props.hasValue || props.selectProps.inputValue || props.isFocused || props.selectProps.menuIsOpen)
      );
    }, [
      props.selectProps.label,
      props.hasValue,
      props.selectProps.inputValue,
      props.isFocused,
      props.selectProps.menuIsOpen,
    ]);

    const placeholder = useMemo(
      () => props.selectProps.placeholder || props.selectProps.label || 'Select...',
      [props.selectProps.placeholder, props.selectProps.label],
    );

    const hideLabel = useMemo(
      () => (!shouldFloatLabel && props.selectProps.inputValue) || props.forFilter,
      [shouldFloatLabel, props.selectProps.inputValue, props.forFilter],
    );

    return (
      <>
        <p
          className={clsx(
            'controlLabel',
            classes.label,
            shouldFloatLabel && 'floatingLabel',
            hideLabel && 'hideLabel',
            selectClasses?.label,
          )}
          {...(dataTestId && { 'data-testid': `${dataTestId}-label` })}
        >
          {props.hasValue ? props.selectProps.label : placeholder}
        </p>

        <components.Control
          {...props}
          className={selectClasses?.control}
          {...(dataTestId && { 'data-testid': dataTestId })}
        >
          {children}
        </components.Control>
      </>
    );
  },
);

export const NewSelect = genericMemo(
  <T, S extends boolean = false>({
    onCreateOption,
    isCreatable,
    isMulti,
    isUserName,
    error,
    isShowDropdownIndicator,
    forFilter,
    virtualize = true,
    startAdornment,
    classes: selectClasses,
    returnArray,
    dataTestId,
    clickableGroupHeaders = false,
    focusOnMount = false,
    changeOnClear = false,
    onChange,
    onGroupHeaderClick,
    ...selectProps
  }: NewSelectProps<T, S>) => {
    const classes = useStyles({});

    const selectRef = useRef<any>(null); // There is typing problem in React-select

    // Fix the infinite loop issue with resize detector
    const startAdornmentRef = useRef<HTMLDivElement>(null);
    const [startAdornmentWidth, setStartAdornmentWidth] = useState<number>(0);

    const styles = useMemo(
      () =>
        selectStyles(
          !error,
          !!selectProps.label,
          selectProps.value?.length !== 0,
          !!isMulti,
          virtualize,
          forFilter,
          startAdornmentWidth,
          focusOnMount,
          clickableGroupHeaders,
        ),
      [
        error,
        selectProps.label,
        selectProps.value?.length,
        isMulti,
        virtualize,
        forFilter,
        startAdornmentWidth,
        focusOnMount,
        clickableGroupHeaders,
      ],
    );

    // Only measure once on mount and when startAdornment changes identity
    useLayoutEffect(() => {
      if (startAdornmentRef.current && startAdornment) {
        const width = startAdornmentRef.current.getBoundingClientRect().width;
        if (width > 0 && width !== startAdornmentWidth) {
          setStartAdornmentWidth(width);
        }
      }
    }, [startAdornment, startAdornmentWidth]);

    // Focus the select on mount if focusOnMount is true
    useEffect(() => {
      if (focusOnMount && selectRef.current) {
        // Use setTimeout to ensure the DOM is fully rendered
        setTimeout(() => {
          selectRef.current?.focus();
        }, 0);
      }
    }, [focusOnMount]);

    const handleChange = useCallback(
      (option: ValueType<SelectOptionType<T>, S>, meta: ActionMeta<SelectOptionType<T>>) => {
        if (onChange) {
          // Handle clear action: when clear icon is clicked, react-select passes null (single) or [] (multi)
          if (meta.action === 'clear' && changeOnClear) {
            // For clear action, pass the cleared value directly
            onChange(option, meta);
            return;
          }

          // Handle returnArray option: wrap single value in array if needed
          if (returnArray && !Array.isArray(option) && option !== null) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            onChange([option], meta);
          } else {
            onChange(option, meta);
          }
        }
      },
      [changeOnClear, returnArray, onChange],
    );

    const GroupHeading = useCallback(
      // react-select passes the group as `data`, which GroupHeadingProps does not declare.
      (props: GroupHeadingProps<SelectOptionType<T>, S> & { data?: GroupOptionType<T> }) => {
        const handleGroupClick = () => {
          if (clickableGroupHeaders && onGroupHeaderClick && props.data && props.data.options) {
            onGroupHeaderClick(props.children as string, props.data.options);
            if (selectRef.current) {
              selectRef.current.blur();
            }
          }
        };

        const handleKeyDown = (event: React.KeyboardEvent) => {
          if (clickableGroupHeaders && (event.key === 'Enter' || event.key === ' ')) {
            event.preventDefault();
            handleGroupClick();
          }
        };

        // react-select v4 forwards unknown props to the heading div; GroupHeadingProps does not declare them.
        const headingHandlers = {
          onClick: clickableGroupHeaders ? handleGroupClick : undefined,
          onKeyDown: clickableGroupHeaders ? handleKeyDown : undefined,
          'aria-label': clickableGroupHeaders ? `Toggle ${props.children} group selection` : undefined,
        };

        return (
          <components.GroupHeading {...props} {...headingHandlers}>
            <Typography variant="h6" component="span" className={classes.categoryName}>
              {props.children}
            </Typography>
          </components.GroupHeading>
        );
      },
      [classes.categoryName, clickableGroupHeaders, onGroupHeaderClick, selectRef],
    );

    const customOption = useCallback(
      ({ children, ...props }: OptionProps<SelectOptionType<T>, S>) => {
        const { data } = props;

        return (
          <components.Option {...props} {...(dataTestId && { 'data-testid': `${dataTestId}-option-${data.label}` })}>
            {isUserName ? <UserIdentifier name={data.label} imageUrl={data.userImgUrl} size="small" /> : children}
          </components.Option>
        );
      },
      [isUserName, dataTestId],
    );

    const CustomControl = useCallback(
      ({ children, ...rest }: ControlProps<SelectOptionType<T>, S>) => (
        <Control {...rest} forFilter={forFilter} classes={selectClasses}>
          {children}
        </Control>
      ),
      [selectClasses, forFilter],
    );

    const CustomSingleValue = useCallback(
      ({ children, ...rest }: SingleValueProps<SelectOptionType<T>>) => {
        return (
          <components.SingleValue
            {...rest}
            className={selectClasses?.singleValue}
            {...(dataTestId && { 'data-testid': `${dataTestId}-single-value` })}
          >
            {children}
          </components.SingleValue>
        );
      },
      [selectClasses, dataTestId],
    );

    const CustomValueContainer = useCallback(
      ({ children, ...rest }: ValueContainerProps<SelectOptionType<T>, S>) => {
        const value = rest.getValue();

        const label = selectProps.name + (value?.length ? `(${value?.length})` : '');

        return (
          <components.ValueContainer
            {...rest}
            className={selectClasses?.valueContainer}
            {...(dataTestId && { 'data-testid': dataTestId + '-value-container' })}
          >
            {forFilter && (
              <div style={{ marginLeft: 8 }}>
                <Typography color="primary">{label}</Typography>
              </div>
            )}

            {startAdornment && <div ref={startAdornmentRef}>{startAdornment}</div>}

            <div
              style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center' }}
              {...(dataTestId && { 'data-testid': `${dataTestId}-value-container-children` })}
            >
              {children}
            </div>
          </components.ValueContainer>
        );
      },
      [forFilter, selectProps.name, startAdornment, selectClasses, dataTestId],
    );

    const CustomClearIndicator = useCallback(
      (props: IndicatorProps<SelectOptionType<T>, S>) =>
        forFilter ? null : (
          <components.ClearIndicator {...props} {...(dataTestId && { 'data-testid': `${dataTestId}-clear` })} />
        ),
      [forFilter, dataTestId],
    );

    const CustomMultiValue = useCallback(
      ({ ...props }: MultiValueProps<SelectOptionType<T>>) => {
        const { data } = props;

        return forFilter ? null : (
          <components.MultiValue
            {...props}
            {...(dataTestId && { 'data-testid': `${dataTestId}-multi-value-${data.label}` })}
          >
            {isUserName ? (
              <UserIdentifier
                name={data.label}
                imageUrl={data.userImgUrl}
                size="large"
                className={classes.multiItemUserIdentifier}
              />
            ) : (
              <div style={{ color: colors.grey160 }}>
                <Typography variant="subtitle1" color="inherit" className={classes.multiItemText}>
                  {data.label}
                </Typography>
              </div>
            )}
          </components.MultiValue>
        );
      },
      [forFilter, isUserName, classes.multiItemText, classes.multiItemUserIdentifier, dataTestId],
    );

    const CustomMultiValueRemove = useCallback(
      ({ ...props }: MultiValueProps<SelectOptionType<T>>) => {
        return (
          <components.MultiValueRemove {...props}>
            <IconButton
              className={classes.multiItemRemoveIconContainer}
              {...(dataTestId && { 'data-testid': `${dataTestId}-multi-value-remove` })}
            >
              <CloseIcon fontSize={12} color={colors.primary.main} />
            </IconButton>
          </components.MultiValueRemove>
        );
      },
      [dataTestId, classes.multiItemRemoveIconContainer],
    );

    const CustomDropdownIndicator = useCallback(
      ({ ...props }: IndicatorProps<SelectOptionType<T>, S>) => {
        return isShowDropdownIndicator === false ? null : (
          <components.DropdownIndicator {...props}>
            {forFilter ? (
              <div style={{ fontSize: '1.125rem', display: 'flex' }}>
                <ArrowDropDown color="primary" fontSize="inherit" />
              </div>
            ) : (
              <components.DownChevron />
            )}
          </components.DropdownIndicator>
        );
      },
      [isShowDropdownIndicator, forFilter],
    );

    const BasicMenuList = useCallback(
      ({ children, ...props }: MenuListComponentProps<SelectOptionType<T> | GroupOptionType<T>, S>) => {
        const menuListRef = useRef<HTMLDivElement>(null);

        // Scroll selected option to top when menu opens
        useEffect(() => {
          if (props.selectProps.menuIsOpen && menuListRef.current) {
            const selectedValue = props.selectProps.value;
            if (selectedValue) {
              // Use requestAnimationFrame to ensure DOM is updated
              requestAnimationFrame(() => {
                if (menuListRef.current) {
                  // Find the selected option element
                  const selectedOption = menuListRef.current.querySelector('[aria-selected="true"]');
                  if (selectedOption && menuListRef.current) {
                    // Scroll the menu list container to show selected option at top
                    menuListRef.current.scrollTop =
                      (selectedOption as HTMLElement).offsetTop - menuListRef.current.offsetTop;
                  }
                }
              });
            }
          }
        }, [props.selectProps.menuIsOpen, props.selectProps.value]);

        return (
          <components.MenuList {...props} innerRef={menuListRef}>
            {children}
          </components.MenuList>
        );
      },
      [],
    );

    const CustomMenuList = useCallback(
      ({
        children,
        ...props
      }: MenuListComponentProps<SelectOptionType<T> | GroupOptionType<T>, S> & { children: ReactElement[] }) => {
        if (!children.length) {
          return <BasicMenuList children={children} {...props} />;
        }

        const listRef = useRef<List>(null);

        // Find index of selected option
        const selectedIndex = useMemo(() => {
          const selectedValue = props.selectProps.value;
          if (!selectedValue) {
            return -1;
          }

          // Handle both single and multi select
          const valuesToFind = Array.isArray(selectedValue) ? selectedValue : [selectedValue];

          for (let i = 0; i < props.options.length; i++) {
            const option = props.options[i];

            // Check if it's a group option
            if (Array.isArray((option as GroupOptionType<T>).options)) {
              // Check if any option in the group matches
              const groupOption = option as GroupOptionType<T>;
              const groupIndex = groupOption.options.findIndex((opt: SelectOptionType<T>) =>
                valuesToFind.some(val => {
                  const valToCompare = Array.isArray(val) ? val : val;
                  return opt.value === (valToCompare as SelectOptionType<T>).value;
                }),
              );
              if (groupIndex !== -1) {
                return i;
              }
            } else {
              // Regular option
              const selectOption = option as SelectOptionType<T>;
              const matches = valuesToFind.some(val => {
                const valToCompare = Array.isArray(val) ? val : val;
                return selectOption.value === (valToCompare as SelectOptionType<T>).value;
              });
              if (matches) {
                return i;
              }
            }
          }

          return -1;
        }, [props.selectProps.value, props.options]);

        // Scroll to selected option when menu opens
        useEffect(() => {
          if (props.selectProps.menuIsOpen && selectedIndex >= 0 && listRef.current) {
            // Use requestAnimationFrame to ensure the list is fully rendered
            requestAnimationFrame(() => {
              listRef.current?.scrollToRow(selectedIndex);
            });
          }
        }, [props.selectProps.menuIsOpen, selectedIndex]);

        const containerRef = useRef<HTMLDivElement>(null);
        const [menuWidth, setMenuWidth] = useState(300);

        useLayoutEffect(() => {
          if (containerRef.current) {
            const width = containerRef.current.offsetWidth;
            if (width > 0) setMenuWidth(width);
          }
        }, []);

        const cache = new CellMeasurerCache({
          // defaultHeight: 40,
          fixedWidth: true,
        });

        const rowRenderer: ListRowRenderer = ({ key, index, style, ...restProps }) => {
          const optionsCount = Array.isArray(props.options[index]?.options) ? props.options[index]?.options?.length : 0;

          return (
            <CellMeasurer cache={cache} columnIndex={0} key={key} rowIndex={index} parent={restProps.parent}>
              {({ measure }) => (
                <div
                  key={key}
                  style={style}
                  onLoad={measure}
                  {...restProps}
                  {...(dataTestId && { 'data-testid': `${dataTestId}-option-${props.options[index]?.label}` })}
                  className={clsx(classes.optionWrapper, optionsCount && 'groupContainer')}
                >
                  {children[index]}
                </div>
              )}
            </CellMeasurer>
          );
        };

        const getRowHeight = (index: { index: number }) => {
          const i = index.index;

          // Group option: keep existing fixed-height behavior
          if (
            Array.isArray(children) &&
            Array.isArray(children[i]?.props?.children) &&
            children[i]?.props?.children?.length
          ) {
            return 40 + children[i].props.children.length * 40;
          }

          const option = props.options[i];
          const label =
            option && !Array.isArray((option as GroupOptionType<T>).options)
              ? (option as SelectOptionType<T>).label
              : '';
          return estimateOptionHeight(label, menuWidth);
        };

        const itemsTotalHeight = Array.isArray(children)
          ? children.reduce((sum, _child, i) => sum + getRowHeight({ index: i }), 0)
          : 0;

        return (
          <div ref={containerRef} style={{ width: '100%' }}>
            <List
              ref={listRef}
              width={1200}
              height={itemsTotalHeight < props.maxHeight ? itemsTotalHeight : props.maxHeight}
              rowHeight={getRowHeight}
              rowCount={children.length}
              rowRenderer={rowRenderer}
              className={classes.virtualOptionsList}
              style={{ maxWidth: '100%' }}
            />
          </div>
        );
      },
      [classes.optionWrapper, classes.virtualOptionsList, dataTestId],
    );

    // Create a modified selectProps that includes the startAdornmentWidth
    const enhancedSelectProps = useMemo(
      () => ({
        ...selectProps,
        startAdornmentWidth, // Pass the width to control
      }),
      [selectProps, startAdornmentWidth],
    );

    // Both menu lists read group options and ReactElement children, which react-select's MenuList type does not model.
    const MenuList = (virtualize ? CustomMenuList : BasicMenuList) as unknown as ComponentType<
      MenuListComponentProps<SelectOptionType<T>, S>
    >;

    return (
      // eslint-disable-next-line react/jsx-no-useless-fragment
      <>
        {isCreatable ? (
          <CreatableSelect<SelectOptionType<T>, S>
            {...enhancedSelectProps}
            ref={selectRef}
            onChange={handleChange}
            inputId={dataTestId}
            className={clsx(classes.selectContainer, selectClasses?.selectContainer)}
            placeholder={selectProps.placeholder || ''}
            styles={styles}
            onCreateOption={onCreateOption}
            captureMenuScroll={false}
            /* eslint-disable  @typescript-eslint/no-explicit-any */
            getOptionValue={option => option.value as any} // There is typing problem in React-select
            getOptionLabel={option => option.label}
            isMulti={isMulti}
            formatOptionLabel={selectProps.formatOptionLabel || FormatOptionLabel}
            components={{
              ClearIndicator: CustomClearIndicator,
              Control: CustomControl,
              DropdownIndicator: CustomDropdownIndicator,
              MenuList: MenuList,
              MultiValue: CustomMultiValue,
              MultiValueRemove: CustomMultiValueRemove,
              Option: customOption,
              Placeholder: () => null,
              ValueContainer: CustomValueContainer,
              SingleValue: CustomSingleValue,
              GroupHeading,
            }}
          />
        ) : (
          <ReactSelect
            {...enhancedSelectProps}
            ref={selectRef}
            onChange={handleChange}
            inputId={dataTestId}
            className={clsx(classes.selectContainer, selectClasses?.selectContainer)}
            placeholder={selectProps.placeholder || ''}
            styles={styles}
            isMulti={isMulti}
            captureMenuScroll={false}
            /* eslint-disable  @typescript-eslint/no-explicit-any */
            getOptionValue={option => option.value as any} // There is typing problem in React-select
            getOptionLabel={option => option.label}
            formatOptionLabel={selectProps.formatOptionLabel || FormatOptionLabel}
            components={{
              ClearIndicator: CustomClearIndicator,
              Control: CustomControl,
              DropdownIndicator: CustomDropdownIndicator,
              MenuList: MenuList,
              MultiValue: CustomMultiValue,
              MultiValueRemove: CustomMultiValueRemove,
              Option: customOption,
              Placeholder: () => null,
              ValueContainer: CustomValueContainer,
              SingleValue: CustomSingleValue,
              GroupHeading,
            }}
          />
        )}
      </>
    );
  },
);
